"use client";

import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import classes from "./datepicker.module.css";
import "./datepicker.css";
import { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import CustomButton from "../buttons/button";
import hu from "date-fns/locale/hu";
import { format, isValid } from "date-fns";
import TextInput from "../inputs/text_input";
import Calendar from '@/assets/svg/icons/calendar.svg';
import ChevronRight from '@/assets/svg/icons/chevron-right.svg';
import ChevronLeft from '@/assets/svg/icons/chevron-left.svg';

const DATE_INPUT_FORMAT = "yyyy.MM.dd.";
const MONTH_SWIPE_THRESHOLD = 48;
const MONTH_OPTIONS = Array.from({ length: 12 }, (_, monthIndex) => ({
    value: monthIndex,
    label: new Date(2020, monthIndex, 1).toLocaleString("hu", { month: "long" }),
}));

const formatDateInput = (value) => value && isValid(value) ? format(value, DATE_INPUT_FORMAT) : "";

const formatDateInputOnType = (value, { isDeletion = false } = {}) => {
    const cleanedValue = value.replace(/[^0-9.\-/\s]/g, "");

    if (!/[.\-/\s]/.test(cleanedValue)) {
        const digitsOnly = cleanedValue.replace(/\D/g, "").slice(0, 8);

        if (digitsOnly.length <= 4) {
            return digitsOnly.length === 4 && !isDeletion ? `${digitsOnly}.` : digitsOnly;
        }

        if (digitsOnly.length <= 6) {
            const withMonth = `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4)}`;
            return digitsOnly.length === 6 && !isDeletion ? `${withMonth}.` : withMonth;
        }

        return `${digitsOnly.slice(0, 4)}.${digitsOnly.slice(4, 6)}.${digitsOnly.slice(6, 8)}`;
    }

    const normalizedWithSeparators = cleanedValue
        .replace(/[\s/-]+/g, ".")
        .replace(/\.{2,}/g, ".");

    if (!isDeletion && /^\d{4}\.\d{2}$/.test(normalizedWithSeparators)) {
        return `${normalizedWithSeparators}.`;
    }

    return normalizedWithSeparators;
};

const parseDateInput = (value) => {
    const compactDigits = value.trim().replace(/\D/g, "");

    const normalizedValue = compactDigits.length === 8
        ? `${compactDigits.slice(0, 4)}.${compactDigits.slice(4, 6)}.${compactDigits.slice(6, 8)}`
        : value.trim().replace(/[\s/-]+/g, ".").replace(/\.+$/g, "");

    const match = normalizedValue.match(/^(\d{4})\.(\d{1,2})\.(\d{1,2})$/);

    if(!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const parsedDate = new Date(year, month - 1, day);

    if(
        parsedDate.getFullYear() !== year ||
        parsedDate.getMonth() !== month - 1 ||
        parsedDate.getDate() !== day
    ) return null;

    return parsedDate;
};

const getYearOptions = (activeYear, allowPastYears = false) => {
    const currentYear = new Date().getFullYear();
    const minYear = allowPastYears ? Math.min(currentYear - 20, activeYear - 5) : currentYear;
    const maxYear = currentYear + 5;

    return Array.from({ length: maxYear - minYear + 1 }, (_, index) => minYear + index);
};

function BodyPopperContainer({ children }) {
    if (typeof document === 'undefined') return children;
    return createPortal(children, document.body);
}

function MobileDateDialogContainer({ children }) {
    if (typeof document === 'undefined') return children;

    return createPortal(
        <div className="mobile-date-dialog" data-mobile-date-dialog role="dialog" aria-modal="true" aria-label="Dátumválasztó">
            {children}
        </div>,
        document.body
    );
}

const CustomInputDate = forwardRef(function CustomInput({custom_value, onClick, custom_onChange, custom_onBlur, label, icon, required_mark, hint, error}, ref) {
    return <div className="padding-bottom-2">
        <TextInput
            className={`w-100`}
            value={custom_value}
            onClick={icon ? undefined : onClick}
            onChange={custom_onChange}
            onBlur={() => custom_onBlur(custom_value)}
            type={"text"}
            label={label}
            help_icon={icon ? Calendar : undefined}
            help_icon_click={icon ? onClick : undefined}
            required_mark={required_mark}
            hint={hint}
            error={error}
        />
    </div>
})

const CustomInputTime = forwardRef(function CustomInput({value, onClick, onChange, label, ...props}, ref) {
    return <div>
        <TextInput
            value={value}
            onClick={onClick}
            onChange={onChange}
            type={"text"}
            label={label}
            {...props}
        />
    </div>
})

export default function CustomDatepicker ({date, setDate, time, setTime, noTime, title, required_mark, enableEmpty, hint, error, horizontal_align, instantDateSelect = false, allowPastYears = false}) {
    const [dateInput, setDateInput] = useState(formatDateInput(date));
    const [datePopup, setDatePopup] = useState(date)
    const [timeInput, setTimeInput] = useState(time ? time.toLocaleTimeString("hu", {hour: '2-digit', minute:'2-digit'}) : new Date().toLocaleTimeString("hu", {hour: '2-digit', minute:'2-digit'}));
    const [timePopup, setTimePopup] = useState(time ? time : new Date());
    const [useMobileDialog, setUseMobileDialog] = useState(false);

    const datepickerRef = useRef();
    const monthNavigationRef = useRef({ decrease: null, increase: null });
    const swipeStartRef = useRef(null);

    // Szinkronizálás ha a date prop külsőleg változik null-ról (pl. localStorage visszatöltés)
    useEffect(() => {
        if (date !== null && datePopup === null) {
            setDateInput(formatDateInput(date));
            setDatePopup(date);
        }
    }, [date]);
    useEffect(() => {
        const updateMobileDialog = () => {
            setUseMobileDialog(window.innerWidth <= 902);
        };

        updateMobileDialog();
        window.addEventListener("resize", updateMobileDialog);

        return () => window.removeEventListener("resize", updateMobileDialog);
    }, []);
    
    const mainDateChangeHandler = (val) => {
        let temp_date;
        if(val === "") {
            // Empty input on blur: keep null if never set, restore last valid date otherwise
            temp_date = (enableEmpty || date === null) ? null : date;
        } else {
            temp_date = parseDateInput(val);
            if(temp_date === null) temp_date = date === null ? null : date;
        }

        setDate(temp_date);
        setDatePopup(temp_date);
        setDateInput(formatDateInput(temp_date));
    }

    const mainTimeChangeHandler = (val) => {
        let temp_time;

        if(isNaN(new Date(`${date.toDateString()} ${val}`).getDate())) temp_time = new Date();
        else temp_time = new Date(`${date.toDateString()} ${val}`);

        setTime(temp_time);
        setTimePopup(temp_time);
        setTimeInput(temp_time.toLocaleTimeString("hu", {hour: '2-digit', minute:'2-digit'}));
    }

    const mainDateOnChangeHandler = (val) => {
        setDatePopup(val);

        if(noTime || instantDateSelect) {
            setDate(val);
            setDateInput(formatDateInput(val));
            datepickerRef.current?.setOpen(false);
        }
    }

    const mainDateInputChangeHandler = (e) => {
        const inputType = e?.nativeEvent?.inputType || "";
        const isDeletion = inputType.includes("delete");

        setDateInput(formatDateInputOnType(e.target.value, { isDeletion }));
    }

    const mainTimeOnChangeHandler = (val) => {
        setTime(val);
        setTimePopup(val)
        setTimeInput(val.toLocaleTimeString("hu", {hour: '2-digit', minute:'2-digit'}));
    }

    const onConfirm = () => {
        setDate(datePopup);
        setDateInput(formatDateInput(datePopup));
        
        if(!noTime) {
            setTime(timePopup);
            setTimeInput(timePopup.toLocaleTimeString("hu", {hour: '2-digit', minute:'2-digit'}))
        }
        datepickerRef.current.setOpen(false);
    }

    const onCancel = () => {
        setDatePopup(date);
        setTimePopup(time)
        datepickerRef.current.setOpen(false);
    }

    const navigateMonth = (event, callback) => {
        event.preventDefault();
        event.stopPropagation();
        callback();
    }

    const keepNativeSelectOpen = (event) => {
        event.stopPropagation();
    }

    const handleCalendarTouchStart = (event) => {
        const touch = event.touches[0];
        swipeStartRef.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    }

    const handleCalendarTouchEnd = (event) => {
        const swipeStart = swipeStartRef.current;
        const touch = event.changedTouches[0];
        swipeStartRef.current = null;

        if(!swipeStart || !touch) return;

        const horizontalDistance = touch.clientX - swipeStart.x;
        const verticalDistance = touch.clientY - swipeStart.y;

        if(
            Math.abs(horizontalDistance) < MONTH_SWIPE_THRESHOLD ||
            Math.abs(horizontalDistance) <= Math.abs(verticalDistance)
        ) return;

        if(horizontalDistance < 0) monthNavigationRef.current.increase?.();
        else monthNavigationRef.current.decrease?.();
    }

    const SwipeableCalendarContainer = ({ className, children }) => (
        <div
            className={className}
            onTouchStart={handleCalendarTouchStart}
            onTouchEnd={handleCalendarTouchEnd}
        >
            {children}
        </div>
    );

    const date_picker_element = <ReactDatePicker
        ref={datepickerRef}
        selected={datePopup}
        onChange={(date) => mainDateOnChangeHandler(date)}
        dateFormat={"yyyy.MM.dd."}
        onCalendarClose={!noTime && !instantDateSelect ? onCancel : undefined}
        shouldCloseOnSelect={noTime || instantDateSelect}
        locale={hu}
        disabledKeyboardNavigation={true}
        autoComplete = "off"
        calendarStartDay={1}
        calendarContainer={SwipeableCalendarContainer}
        customInput={<CustomInputDate custom_value={dateInput} custom_onChange={(e) => mainDateInputChangeHandler(e)} custom_onBlur={(date) => mainDateChangeHandler(date)} label={title} icon required_mark={required_mark} hint={hint} error={error} />}
        showPopperArrow={false}
        calendarClassName={"custom-date"}
        popperContainer={useMobileDialog ? MobileDateDialogContainer : BodyPopperContainer}
        wrapperClassName="d-flex align-self-stretch custom-date-wrapper"
        popperClassName="custom-date-popper"
        weekDayClassName={(date) => `${classes["days"]} text-sm-medium`}
        dayClassName={(day) => {
            return `${classes["days"]} text-sm-medium`
        }}
        popperPlacement="bottom-end"

        renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
                changeYear,
                changeMonth,
            }) => {
                monthNavigationRef.current = { decrease: decreaseMonth, increase: increaseMonth };

                return <div className="d-flex flex-column align-self-stretch gap-2">
                    <div className="custom-date-header d-flex align-items-center justify-content-between">
                        <CustomButton size="md" hierarchy="tertiary-gray" icon="only" is_svg={true} svg={ChevronLeft} type="button" onClick={(event) => navigateMonth(event, decreaseMonth)} />
                        <div className="custom-date-header-selects">
                            <select className="custom-date-header-select text-sm-medium" value={monthDate.getMonth()} onPointerDown={keepNativeSelectOpen} onMouseDown={keepNativeSelectOpen} onTouchStart={keepNativeSelectOpen} onClick={keepNativeSelectOpen} onChange={(event) => changeMonth(Number(event.target.value))}>
                                {MONTH_OPTIONS.map(month => <option key={month.value} value={month.value}>{month.label}</option>)}
                            </select>
                            <select className="custom-date-header-select custom-date-header-year text-sm-medium" value={monthDate.getFullYear()} onPointerDown={keepNativeSelectOpen} onMouseDown={keepNativeSelectOpen} onTouchStart={keepNativeSelectOpen} onClick={keepNativeSelectOpen} onChange={(event) => changeYear(Number(event.target.value))}>
                                {getYearOptions(monthDate.getFullYear(), allowPastYears).map(year => <option key={year} value={year}>{year}</option>)}
                            </select>
                        </div>
                        <CustomButton size="md" hierarchy="tertiary-gray" icon="only" is_svg={true} svg={ChevronRight} type="button" onClick={(event) => navigateMonth(event, increaseMonth)} />
                    </div>
                    {!noTime && !instantDateSelect && <div className={`${classes["popup-container"]} datepicker-no-time`}>
                        <ReactDatePicker
                            selected={datePopup}
                            onChange={(date) => setDatePopup(date)}
                            dateFormat={"MMMM dd."}
                            locale={hu}
                            disabled
                            customInput={<CustomInputTime label={"Dátum"} disabled style={{width: (noTime ? "100%" : 150)}} />}
                        />
                        {!noTime && <ReactDatePicker
                            selected={timePopup}
                            onChange={date => setTimePopup(date)}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Time"
                            dateFormat="HH:mm"
                            timeFormat="HH:mm"
                            showPopperArrow={false}
                            calendarClassName={"custom-time"}
                            popperPlacement="bottom-start"
                            timeClassName={date => {
                                if(date.getHours() === timePopup.getHours() && date.getMinutes() === timePopup.getMinutes()) return `${classes["time"]} ${classes["selected-time"]} text-sm-medium`;
                                else return `${classes["time"]} text-sm-medium`;
                            }}
                            customInput={<CustomInputTime label={"Idő"} disabled style={{width: 100}} />}
                        />}
                    </div>}
                </div>
            }}
    >   
        {!noTime && !instantDateSelect && <div className={classes["datepicker-footer"]}>
            <CustomButton className="w-50" size="lg" hierarchy="primary"  onClick={onConfirm} >Alkalmaz</CustomButton>
            <CustomButton className="w-50" size="lg" hierarchy="secondary-gray"  onClick={onCancel} >Mégsem</CustomButton>
        </div>}
    </ReactDatePicker>

    const time_picker_element = <ReactDatePicker
        selected={time}
        onChange={date => mainTimeOnChangeHandler(date)}
        showTimeSelect
        showTimeSelectOnly
        timeIntervals={30}
        timeCaption="Time"
        dateFormat="HH:mm"
        timeFormat="HH:mm"
        showPopperArrow={false}
        calendarClassName={"custom-time"}
    popperContainer={useMobileDialog ? BodyPopperContainer : undefined}
    wrapperClassName="d-flex align-self-stretch custom-time-wrapper"
    popperClassName="custom-time-popper"
        popperPlacement={useMobileDialog ? "top-start" : "bottom-start"}
        timeClassName={date => {
            if(date.getHours() === time.getHours() && date.getMinutes() === time.getMinutes()) return `${classes["time"]} ${classes["selected-time"]} text-sm-medium`;
            else return `${classes["time"]} text-sm-medium`;
        }}
        customInput={<CustomInputDate custom_value={timeInput} custom_onChange={(e) => setTimeInput(e.target.value)} custom_onBlur={(date) => mainTimeChangeHandler(date)} label={"Idő"} />}
    />

    return <div className={`${classes["datepicker-container"]}`}>
        {horizontal_align
            ? <div className="d-flex flex-row align-items-end gap-2 w-100 align-self-stretch">
                <div className={`${classes["horizontal-align-input-container"]} ${noTime ? classes["no-time"] : ""}`}>{date_picker_element}</div>
                {!noTime && <div className={`${classes["horizontal-align-input-container"]}`}>{time_picker_element}</div>}
            </div>
            : <>
                {date_picker_element}
                {!noTime && time_picker_element}
            </>
        }
    </div>
}